import React, {useEffect, useMemo, useState} from "react";
import {Box, Stack, Typography} from "@mui/material";
import {alpha, useTheme} from "@mui/material/styles";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import UpcomingRoundedIcon from "@mui/icons-material/UpcomingRounded";
import CelebrationRoundedIcon from "@mui/icons-material/CelebrationRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {EmployeeRestControllerApiFactory, HolidayList} from "arteco-api-client-ts";
import dayjs from "dayjs";
import {useApiManager} from "../../../components/utils/api";
import {EmptyState, FilterChips, Page, PageHeader, Panel, SkeletonRows, SoftChip, StatGrid, StatTile} from "../../../components/kit";

const CALENDAR: Record<string, string> = {PALMA: "Palma", MADRID: "Madrid"};

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.substring(1);

/**
 * Festivos del año que te tocan según tu calendario de trabajo (#13900): los generales y los de tu
 * calendario, los mismos que marca el calendario de la portada. El del año siguiente sale en cuanto
 * RR. HH. lo carga. Solo lectura.
 */
export const EmployeeHolidaysPage = () => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(EmployeeRestControllerApiFactory);
	const thisYear = dayjs().year();
	const [byYear, setByYear] = useState<Record<number, HolidayList[]>>();
	const [year, setYear] = useState(thisYear);

	useEffect(() => {
		Promise.all([thisYear, thisYear + 1].map(y => apiManager.execute(ctrl.getEmployeeOwnHolidays(y)).catch(() => [] as HolidayList[])))
			.then(([actual, next]) => setByYear({[thisYear]: actual || [], [thisYear + 1]: next || []}));
	}, []);

	const today = dayjs().format("YYYY-MM-DD");
	const list = byYear?.[year] || [];
	const upcoming = list.filter(h => h.holidayDate >= today);
	const next = upcoming[0];
	const years = byYear ? [thisYear, thisYear + 1].filter(y => y === thisYear || (byYear[y] || []).length > 0) : [thisYear];

	return (
		<Page>
			<PageHeader title="Festivos"
						subtitle="Los que te aplican según tu calendario de trabajo; son los mismos que marca el calendario de la portada."/>

			<StatGrid>
				<StatTile loading={!byYear} icon={<EventRoundedIcon fontSize="small"/>} tone="info"
						  label={`Festivos en ${year}`} value={list.length}/>
				<StatTile loading={!byYear} icon={<UpcomingRoundedIcon fontSize="small"/>} tone={upcoming.length ? "success" : "neutral"}
						  label="Quedan este año" value={year === thisYear ? upcoming.length : list.length}/>
				<StatTile loading={!byYear} icon={<CelebrationRoundedIcon fontSize="small"/>} tone="neutral"
						  label={next ? next.description : "Próximo festivo"}
						  value={next ? dayjs(next.holidayDate).format("D MMM") : "—"}/>
			</StatGrid>

			<Panel title={`Calendario de ${year}`}
				   subtitle="Los ya pasados salen atenuados."
				   action={years.length > 1 && (
					   <FilterChips value={year} onChange={setYear}
									options={years.map(y => ({value: y, label: String(y)}))}/>
				   )}>
				{!byYear ? <SkeletonRows rows={6}/> : list.length === 0 ? (
					<EmptyState icon={<EventRoundedIcon/>} title={`No hay festivos cargados para ${year}`}
								description="Los da de alta RR. HH.; si falta alguno, avísales."/>
				) : (
					<HolidayRows list={list} today={today} nextId={next?.id}/>
				)}
				<Stack direction="row" spacing={1} sx={{mt: 2, color: "text.secondary"}}>
					<InfoOutlinedIcon sx={{fontSize: 16, mt: "2px"}}/>
					<Typography variant="caption">
						Los festivos no cuentan como días de vacaciones. Los marcados con una ciudad solo se aplican a quien trabaja con ese calendario.
					</Typography>
				</Stack>
			</Panel>
		</Page>
	);
};

const HolidayRows = ({list, today, nextId}: { list: HolidayList[], today: string, nextId?: number }) => {
	const theme = useTheme();
	const byMonth = useMemo(() => {
		const map = new Map<string, HolidayList[]>();
		list.forEach(h => {
			const key = dayjs(h.holidayDate).format("MMMM");
			map.set(key, [...(map.get(key) || []), h]);
		});
		return Array.from(map.entries());
	}, [list]);

	return (
		<Stack spacing={2}>
			{byMonth.map(([month, items]) => (
				<Box key={month}>
					<Typography variant="caption" color="text.secondary" fontWeight={700} sx={{display: "block", mb: 0.75}}>
						{capitalize(month)}
					</Typography>
					<Stack spacing={1}>
						{items.map(h => {
							const d = dayjs(h.holidayDate);
							const past = h.holidayDate < today;
							const weekend = d.day() === 0 || d.day() === 6;
							const isNext = h.id === nextId;
							return (
								<Stack key={h.id || h.holidayDate} direction="row" spacing={1.5} alignItems="center" sx={{
									p: 1.25, borderRadius: 2, border: "1px solid",
									borderColor: isNext ? alpha(theme.palette.primary.main, 0.5) : "divider",
									bgcolor: isNext ? alpha(theme.palette.primary.main, 0.04) : "background.paper",
									opacity: past ? 0.5 : 1,
								}}>
									<Box sx={{
										width: 48, flexShrink: 0, textAlign: "center", borderRadius: 1.5, py: 0.5,
										bgcolor: alpha(theme.palette.info.main, past ? 0.05 : 0.1), color: past ? "text.secondary" : "info.dark",
									}}>
										<Typography variant="h6" fontWeight={700} sx={{lineHeight: 1.1, fontVariantNumeric: "tabular-nums"}}>{d.date()}</Typography>
										<Typography variant="caption" sx={{lineHeight: 1}}>{d.format("MMM").replace(".", "")}</Typography>
									</Box>
									<Box sx={{minWidth: 0, flexGrow: 1}}>
										<Typography variant="body2" fontWeight={600} noWrap>{h.description}</Typography>
										<Typography variant="caption" color="text.secondary">
											{capitalize(d.format("dddd"))}{weekend ? " · cae en fin de semana" : ""}
										</Typography>
									</Box>
									<Stack direction="row" spacing={0.75} sx={{flexShrink: 0}}>
										{isNext && <SoftChip tone="info" label="Próximo"/>}
										{h.calendar && <SoftChip tone="neutral" label={CALENDAR[h.calendar] || h.calendar}/>}
									</Stack>
								</Stack>
							);
						})}
					</Stack>
				</Box>
			))}
		</Stack>
	);
};
